import { FaGithub, FaLinkedinIn } from "react-icons/fa";
import { FiMapPin, FiBookOpen, FiCode, FiActivity } from "react-icons/fi";
import { Reveal, MaskText } from "./ui/motion";
import Marquee from "./Marquee";
import { Panel, Parallax } from "../journey/Panel";
import ProfileImage from "../image/portfolio-black-removebg-preview.png";

const META = [
  {
    icon: <FiMapPin />,
    label: "Location",
    value: "Naousa, Imathia — Greece",
  },
  {
    icon: <FiBookOpen />,
    label: "Education",
    value: "MSc Information & Electronic Engineering · Int'l Hellenic University (2022)",
  },
  {
    icon: <FiCode />,
    label: "Stack",
    value: "Vue · React · Next.js · JavaScript · TypeScript · C# · SQL",
  },
  {
    icon: <FiActivity />,
    label: "Off the clock",
    value: "Basketball · MMA · Lifting at the gym",
  },
];

function RoleMarquee() {
  return (
    <Marquee
      duration={28}
      items={[
        <span className="display text-3xl sm:text-5xl">Creative Coder</span>,
        <span className="display italic text-3xl sm:text-5xl text-acid">Front-End Engineer</span>,
        <span className="display text-3xl sm:text-5xl text-stroke">UI Engineer</span>,
        <span className="display italic text-3xl sm:text-5xl">Motion Lover</span>,
      ]}
    />
  );
}

function SectionHeader() {
  return (
    <div className="flex items-center gap-4 mb-12">
      <span className="eyebrow">(01)</span>
      <span className="h-px w-12 bg-acid" />
      <span className="eyebrow">About Me</span>
    </div>
  );
}

function Heading({ horizontal }) {
  return (
    <MaskText
      className={`display leading-[1.02] max-w-5xl ${
        horizontal ? "text-[6.5vw]" : "text-[8vw] mdl:text-[5.2vw] mb-20"
      }`}
      lineClassName="text-paper"
      lines={[
        <>An engineer from the <span className="italic text-acid">heroic city</span></>,
        <>of Naousa who builds things</>,
        <>for the <span className="italic">web.</span></>,
      ]}
    />
  );
}

function Portrait({ horizontal }) {
  return (
    <div className="relative">
      <div className="absolute -top-3 -left-3 w-16 h-16 border-t border-l border-acid" />
      <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b border-r border-acid" />
      <div className="overflow-hidden bg-ink-soft border border-paper/10">
        <img
          src={ProfileImage}
          alt="Tsiakiris Nikos"
          className={`w-full object-contain ${horizontal ? "max-h-[52vh]" : ""}`}
        />
      </div>
      <div className="mt-4 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-paper-dim">
        <span>Tsiakiris Nikos</span>
        <span>EST. 2022</span>
      </div>
    </div>
  );
}

function BioOne() {
  return (
    <p className="text-lg sm:text-xl leading-relaxed text-paper/85">
      I finished my MSc (integrated master's) in Information and
      Electronic Engineering at the International Hellenic University
      in Thessaloniki back in 2022. I mostly work in JavaScript, Java
      and SQL, and I've spent a good amount of time with{" "}
      <span className="text-acid">React</span> and{" "}
      <span className="text-acid">Next.js</span> — plus a bit of
      MongoDB on the backend side.
    </p>
  );
}

function BioTwo() {
  return (
    <p className="text-base sm:text-lg leading-relaxed text-paper-dim">
      Right now I'm a Front-End Engineer, working mostly with{" "}
      <span className="text-acid">Vue</span> and{" "}
      <span className="text-acid">C#</span> on real production apps. I
      care about how an interface actually feels to use — that it's
      fast and stays out of your way. When I'm away from the keyboard
      I'm usually on the basketball court, training MMA, or at the gym.
    </p>
  );
}

function MetaGrid({ compact }) {
  return (
    <div className="grid sm:grid-cols-2 gap-px bg-paper/10 border border-paper/10">
      {META.map((m, i) => (
        <Reveal key={i} delay={0.08 * i}>
          <div className={`bg-ink h-full flex flex-col gap-2 ${compact ? "p-4" : "p-5"}`}>
            <div className="flex items-center gap-2 text-acid text-sm">
              {m.icon}
              <span className="eyebrow text-paper-dim">{m.label}</span>
            </div>
            <p className="text-sm text-paper/90 leading-snug">{m.value}</p>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

function Socials() {
  return (
    <div className="flex items-center gap-4">
      <a
        href="https://github.com/ntsiakiris"
        target="_blank"
        rel="noopener noreferrer"
        data-cursor-label="github"
        className="w-12 h-12 grid place-items-center rounded-full border border-paper/20 text-paper hover:bg-acid hover:text-ink hover:border-acid transition-colors duration-300"
      >
        <FaGithub size={18} />
      </a>
      <a
        href="https://www.linkedin.com/in/nikolaos-tsiakiris-b047a2229/"
        target="_blank"
        rel="noopener noreferrer"
        data-cursor-label="linkedin"
        className="w-12 h-12 grid place-items-center rounded-full border border-paper/20 text-paper hover:bg-acid hover:text-ink hover:border-acid transition-colors duration-300"
      >
        <FaLinkedinIn size={18} />
      </a>
    </div>
  );
}

export default function About({ horizontal }) {
  if (horizontal) {
    return (
      <section id="about" className="flex h-full shrink-0">
        {/* Scene 1 — statement */}
        <Panel horizontal className="flex flex-col justify-center">
          <div className="border-y border-paper/10 py-5 mb-14">
            <RoleMarquee />
          </div>
          <div className="px-6 sm:px-10">
            <SectionHeader />
            <Parallax horizontal depth={-0.05}>
              <Heading horizontal />
            </Parallax>
          </div>
        </Panel>

        {/* Scene 2 — portrait + bio */}
        <Panel horizontal className="flex items-center px-10 xl:px-16">
          <div className="grid grid-cols-[0.75fr_1.25fr] gap-14 xl:gap-20 items-center w-full">
            <Parallax horizontal depth={0.06}>
              <Reveal>
                <Portrait horizontal />
              </Reveal>
            </Parallax>
            <div className="flex flex-col gap-7">
              <Reveal delay={0.1}>
                <BioOne />
              </Reveal>
              <Reveal delay={0.18}>
                <BioTwo />
              </Reveal>
              <MetaGrid compact />
              <Reveal delay={0.1}>
                <Socials />
              </Reveal>
            </div>
          </div>
        </Panel>
      </section>
    );
  }

  return (
    <section id="about" className="relative py-24 sm:py-36">
      {/* role marquee */}
      <div className="border-y border-paper/10 py-5 mb-20 sm:mb-32">
        <RoleMarquee />
      </div>

      <div className="px-6 sm:px-10">
        <SectionHeader />
        <Heading />

        <div className="grid lgl:grid-cols-[0.85fr_1.15fr] gap-12 lgl:gap-20 items-start">
          <Reveal>
            <Portrait />
          </Reveal>

          <div className="flex flex-col gap-10">
            <Reveal delay={0.1}>
              <BioOne />
            </Reveal>
            <Reveal delay={0.18}>
              <BioTwo />
            </Reveal>
            <MetaGrid />
            <Reveal delay={0.1}>
              <Socials />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
